import { Suspense } from 'react';

import { Navigate, Route } from 'react-router-dom';

import { PageSkeleton } from '@gw/ui';
import {
  DatasetTestRedirect,
  DeveloperDatasetEditRedirect,
  DeveloperDatasetQueriesRedirect,
  DeveloperDatasetQueryDetailRedirect,
  DeveloperDatasetTestQueryRedirect,
} from '@/components/routes/DatasetRedirect';
import { FeatureFlagGuard } from '@/components/routes/FeatureFlagGuard';
import { RoleGuard } from '@/components/routes/RoleGuard';
import { lazyWithRetry } from '@/lib/lazyWithRetry.util';

// ============================================================================
// /publisher/* routes (workspace shell, dataset management, service creation,
// application management) plus the retired /developer/* family, which lives
// here because every one of those paths is now a back-compat redirect into
// /publisher/* (or /organization/provider-agreement — GW-8103). Mounted
// inside ProtectedRoute → TOSGuard → AppLayout in App.tsx; the guards live
// on that shared scaffold, not here.
// ============================================================================

// Developer Pages
// GW-8139: PublisherSummaryPage is now imported directly by
// PublisherWorkspacePage (the shared /publisher tab shell) rather than
// lazily here.
// GW-8139: Shared TabbedDetailPage shell for /publisher (target IA §6).
const PublisherWorkspacePage = lazyWithRetry(() =>
  import('@/pages/developer/PublisherWorkspacePage').then((m) => ({
    default: m.PublisherWorkspacePage,
  }))
);
// GW-8284: ApiKeyPage's standalone /developer/api-key route is deleted —
// PublisherWorkspacePage now imports ApiKeyPage directly for its "api-key" tab.
// GW-8103: DeveloperServicesPage's standalone /developer/services route was
// renamed to /publisher/services (backward-compat redirect only) — the page
// is now rendered directly by PublisherWorkspacePage's "services" tab, so it
// is no longer lazily imported here.
const DatasetCreatePage = lazyWithRetry(() =>
  import('@/pages/developer/DatasetCreatePage').then((m) => ({ default: m.DatasetCreatePage }))
);
const DatasetEditPage = lazyWithRetry(() =>
  import('@/pages/developer/DatasetEditPage').then((m) => ({ default: m.DatasetEditPage }))
);
// GW-1453: Dataset test query page
const DatasetTestQueryPage = lazyWithRetry(() =>
  import('@/pages/developer/DatasetTestQueryPage').then((m) => ({
    default: m.DatasetTestQueryPage,
  }))
);
// NOTE: also declared in publisher.routes.tsx — identical import path, so Vite
// emits a single chunk; per-file wrappers keep the route files decoupled.
// GW-1343: Query detail page
const QueryDetailPage = lazyWithRetry(() =>
  import('@/pages/datasets/QueryDetailPage').then((m) => ({ default: m.QueryDetailPage }))
);
// GW-1343: Dataset query history page (admin/publisher view)
const DatasetQueryHistoryPage = lazyWithRetry(() =>
  import('@/pages/datasets/DatasetQueryHistoryPage').then((m) => ({
    default: m.DatasetQueryHistoryPage,
  }))
);
// GW-8103: OrgDatasetsManagementPage's standalone /developer/datasets route
// was renamed to /publisher/datasets (backward-compat redirect only) — the
// page is now rendered directly by PublisherWorkspacePage's "datasets" tab,
// so it is no longer lazily imported here.
// GW-1459: Organization query history page (all datasets)
const OrgQueryHistoryPage = lazyWithRetry(() =>
  import('@/pages/datasets/OrgQueryHistoryPage').then((m) => ({
    default: m.OrgQueryHistoryPage,
  }))
);
// NOTE: also declared in organization.routes.tsx — identical import path, so
// Vite emits a single chunk; per-file wrappers keep the route files decoupled.
const ServiceWizardPage = lazyWithRetry(() =>
  import('@/pages/organization/ServiceWizardPage').then((m) => ({
    default: m.ServiceWizardPage,
  }))
);
// Application Pages
const CreateApplicationPage = lazyWithRetry(() =>
  import('@/pages/applications/CreateApplicationPage').then((m) => ({
    default: m.CreateApplicationPage,
  }))
);
const EditApplicationPage = lazyWithRetry(() =>
  import('@/pages/applications/EditApplicationPage').then((m) => ({
    default: m.EditApplicationPage,
  }))
);
const ApplicationAnalyticsPage = lazyWithRetry(() =>
  import('@/pages/applications/ApplicationAnalyticsPage').then((m) => ({
    default: m.ApplicationAnalyticsPage,
  }))
);
// GW-5069: Application setup page (API key + SDK configuration)
const ApplicationSetupPage = lazyWithRetry(() =>
  import('@/pages/applications/ApplicationSetupPage').then((m) => ({
    default: m.ApplicationSetupPage,
  }))
);
// GW-2510: Item create/edit pages
const CreateItemPage = lazyWithRetry(() =>
  import('@/pages/applications/CreateItemPage').then((m) => ({
    default: m.CreateItemPage,
  }))
);
const EditItemPage = lazyWithRetry(() =>
  import('@/pages/applications/EditItemPage').then((m) => ({
    default: m.EditItemPage,
  }))
);

export const publisherRoutes = (
  <>
    <Route
      path="/developer/provider-agreement"
      element={<Navigate to="/organization/provider-agreement" replace />}
    />
    {/* GW-8103: The standalone /developer/datasets/* publisher
        routes were renamed to /publisher/datasets/* (target IA).
        These are now param-preserving backward-compat redirects
        so old bookmarked URLs keep working; the real pages mount
        under /publisher/datasets/* below. Routing-only rename —
        page rebuilds are out of scope. */}
    <Route path="/developer/datasets" element={<Navigate to="/publisher/datasets" replace />} />
    <Route
      path="/developer/datasets/create"
      element={<Navigate to="/publisher/datasets/create" replace />}
    />
    <Route path="/developer/datasets/:datasetId/edit" element={<DeveloperDatasetEditRedirect />} />
    <Route
      path="/developer/datasets/:datasetId/test-query"
      element={<DeveloperDatasetTestQueryRedirect />}
    />
    {/* GW-1728: Redirect old test path (now to /publisher/*) */}
    <Route path="/developer/datasets/:datasetId/test" element={<DatasetTestRedirect />} />
    <Route
      path="/developer/datasets/queries"
      element={<Navigate to="/publisher/datasets/queries" replace />}
    />
    <Route
      path="/developer/datasets/:datasetId/queries"
      element={<DeveloperDatasetQueriesRedirect />}
    />
    <Route
      path="/developer/datasets/:datasetId/queries/:queryId"
      element={<DeveloperDatasetQueryDetailRedirect />}
    />

    {/* GW-2425/GW-8284: /developer/api-key deleted — /publisher/api-key
        (PublisherWorkspacePage's "api-key" tab) is now the sole
        canonical API key management location. */}

    {/* GW-8103: /developer/services renamed to /publisher/services
        (target IA). Backward-compat redirect; /publisher/services
        resolves via PublisherWorkspacePage's "services" tab. */}
    <Route path="/developer/services" element={<Navigate to="/publisher/services" replace />} />

    <Route path="/publisher" element={<Navigate to="/publisher/summary" replace />} />
    <Route
      path="/publisher/:tab"
      element={
        <RoleGuard
          requiredRoles={['app_admin', 'org_admin', 'dataset_admin', 'service_admin', 'consultant']}
        >
          <Suspense fallback={<PageSkeleton />}>
            <PublisherWorkspacePage />
          </Suspense>
        </RoleGuard>
      }
    />

    {/* GW-8264: /publisher/* sub-routes — wire the existing
        dataset/service page components to their target-IA
        paths (06 audit finding). Routing only: reuses the same
        components, feature-flag guards, and role guards as
        their /developer and /organization equivalents. */}
    <Route
      path="/publisher/datasets/create"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/publisher/datasets">
          <RoleGuard requiredRoles={['app_admin', 'org_admin', 'dataset_admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <DatasetCreatePage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    <Route
      path="/publisher/datasets/queries"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/publisher/datasets">
          <RoleGuard requiredRoles={['dataset_admin', 'app_admin', 'org_admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <OrgQueryHistoryPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    <Route
      path="/publisher/datasets/:datasetId/test-query"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/publisher/datasets">
          <RoleGuard requiredRoles={['dataset_admin', 'app_admin', 'org_admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <DatasetTestQueryPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    {/* GW-8103: /publisher/datasets/* routes renamed from the
        retired standalone /developer/datasets/* family. Reuse the
        exact same FeatureFlagGuard/RoleGuard stacks as their
        /developer twins (now redirects above). */}
    {/* GW-1239: Dataset edit page for admin users (gated by feature flag) */}
    <Route
      path="/publisher/datasets/:datasetId/edit"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/publisher/datasets">
          <RoleGuard requiredRoles={['app_admin', 'org_admin', 'dataset_admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <DatasetEditPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    {/* GW-1728: Redirect old test path */}
    <Route path="/publisher/datasets/:datasetId/test" element={<DatasetTestRedirect />} />
    {/* GW-1343: Dataset query history page for admin/publisher users (gated by feature flag) */}
    <Route
      path="/publisher/datasets/:datasetId/queries"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/publisher/datasets">
          <RoleGuard requiredRoles={['app_admin', 'org_admin', 'dataset_admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <DatasetQueryHistoryPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    {/* GW-1343: Query detail page for admin/publisher users (gated by feature flag) */}
    <Route
      path="/publisher/datasets/:datasetId/queries/:queryId"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/publisher/datasets">
          <RoleGuard requiredRoles={['app_admin', 'org_admin', 'dataset_admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <QueryDetailPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    {/* GW-8665: gated behind services_marketplace. */}
    <Route
      path="/publisher/services/create"
      element={
        <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
          <RoleGuard requiredRoles={['org_admin', 'service_admin', 'consultant']}>
            <Suspense fallback={<PageSkeleton />}>
              <ServiceWizardPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    {/* GW-8282/GW-8286: /publisher/applications/* sub-routes —
        the sole surviving home for these page components; the
        equivalent legacy /developer prefix route family they
        used to also serve was retired per target-ia-outline.md
        §12 (target-IA paths per §3.3/§6). */}
    <Route
      path="/publisher/applications/create"
      element={
        <RoleGuard requiredRoles={['app_admin', 'org_admin']}>
          <Suspense fallback={<PageSkeleton />}>
            <CreateApplicationPage />
          </Suspense>
        </RoleGuard>
      }
    />
    <Route
      path="/publisher/applications/:id/edit"
      element={
        <RoleGuard requiredRoles={['app_admin', 'org_admin']}>
          <Suspense fallback={<PageSkeleton />}>
            <EditApplicationPage />
          </Suspense>
        </RoleGuard>
      }
    />
    <Route
      path="/publisher/applications/:id/items/new"
      element={
        <RoleGuard requiredRoles={['app_admin', 'org_admin']}>
          <Suspense fallback={<PageSkeleton />}>
            <CreateItemPage />
          </Suspense>
        </RoleGuard>
      }
    />
    <Route
      path="/publisher/applications/:id/items/:itemId"
      element={
        <RoleGuard requiredRoles={['app_admin', 'org_admin']}>
          <Suspense fallback={<PageSkeleton />}>
            <EditItemPage />
          </Suspense>
        </RoleGuard>
      }
    />
    <Route
      path="/publisher/applications/:appId/analytics"
      element={
        <FeatureFlagGuard flagName="application_analytics" fallbackRoute="/publisher/applications">
          <RoleGuard requiredRoles={['app_admin', 'org_admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <ApplicationAnalyticsPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    <Route
      path="/publisher/applications/:appId/setup"
      element={
        <RoleGuard requiredRoles={['app_admin', 'org_admin']}>
          <Suspense fallback={<PageSkeleton />}>
            <ApplicationSetupPage />
          </Suspense>
        </RoleGuard>
      }
    />
  </>
);
